import debounce from "lodash/debounce";
import { Movie } from "@components/movie/Movie";

// window.Lightbox is already globally typed in source/types/index.d.ts

// Scene7 hotspot overlays (div.s7mapoverlay) only wire up mouse events internally; on touch
// devices the underlying zoom/pan view swallows the touch gesture before a synthetic click can
// fire, so taps never reach a plain "click" listener. Pointer Events unify mouse/touch/pen into a
// single pointerdown/pointerup pair; the distance/duration check tells a tap apart from a pan/pinch
// gesture that happens to start or end on top of the hotspot.
const HOTSPOT_TAP_MOVE_THRESHOLD_PX = 10;
const HOTSPOT_TAP_MAX_DURATION_MS = 500;

// ─── Class ────────────────────────────────────────────────────────────────────

export class LightboxController {
  private el: HTMLElement;
  private abortController: AbortController;
  // Separate controller for popup events — aborted & recreated on each attachPopupEvents() call
  private popupAbortController: AbortController;

  private popupContainer: HTMLElement | null;
  private movieUrl: string | undefined;
  private hotspotPointerTarget: HTMLElement | null = null;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private pointerStartTime = 0;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.popupAbortController = new AbortController();
    this.popupContainer = el.querySelector<HTMLElement>("#popup");
  }

  init(): void {
    const { signal } = this.abortController;

    // viewportChange (Terrific) → debounced resize
    window.addEventListener(
      "resize",
      debounce(() => this.removeLastMargin(), 200),
      { signal },
    );

    // attachPopupEvents custom event (fired by other modules to re-attach popup handlers)
    document.addEventListener("attachPopupEvents", () => this.attachPopupEvents(), { signal });

    this.attachPopupEvents();
  }

  destroy(): void {
    this.popupAbortController.abort();
    this.abortController.abort();
  }

  // ─── Popup event binding ─────────────────────────────────────────────────────

  private attachPopupEvents(): void {
    // Abort previous popup events and create a fresh signal — prevents duplicate handlers
    // (mirrors the original's $("body").off("click.lightbox").on(...) pattern)
    this.popupAbortController.abort();
    this.popupAbortController = new AbortController();
    const { signal } = this.popupAbortController;

    // Popup links (a[href*='popups']) — native anchors, plain click works on every input type
    document.addEventListener(
      "click",
      (e: MouseEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLElement>("a[href*='popups']");
        if (currentTarget) {
          e.preventDefault();
          this.openPopup(e, currentTarget).catch(console.error);
        }
      },
      { signal },
    );

    // Hotspot overlays (div.s7mapoverlay[data-href*='popups']) — Pointer Events, not click
    document.addEventListener(
      "pointerdown",
      (e: PointerEvent) => {
        // Ignore multi-touch/pinch (non-primary pointers) and non-left-button mouse presses
        if (!e.isPrimary || e.button !== 0) return;

        const currentTarget = (e.target as Element).closest<HTMLElement>(
          "div.s7mapoverlay[data-href*='popups']",
        );
        if (!currentTarget) return;

        this.hotspotPointerTarget = currentTarget;
        this.pointerStartX = e.clientX;
        this.pointerStartY = e.clientY;
        this.pointerStartTime = e.timeStamp;
      },
      { signal },
    );

    document.addEventListener(
      "pointerup",
      (e: PointerEvent) => {
        const currentTarget = this.hotspotPointerTarget;
        this.hotspotPointerTarget = null;
        if (!currentTarget) return;

        const deltaX = Math.abs(e.clientX - this.pointerStartX);
        const deltaY = Math.abs(e.clientY - this.pointerStartY);
        const elapsed = e.timeStamp - this.pointerStartTime;
        const isTap =
          deltaX < HOTSPOT_TAP_MOVE_THRESHOLD_PX &&
          deltaY < HOTSPOT_TAP_MOVE_THRESHOLD_PX &&
          elapsed < HOTSPOT_TAP_MAX_DURATION_MS;

        if (isTap) {
          e.preventDefault();
          this.openPopup(e, currentTarget).catch(console.error);
        }
      },
      { signal },
    );

    // Gesture interrupted (e.g. scroll/pinch takes over) — discard the pending tap
    document.addEventListener(
      "pointercancel",
      () => {
        this.hotspotPointerTarget = null;
      },
      { signal },
    );

    // Hotspot overlay — keyboard (Space / Enter)
    document.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLElement>(
          "div.s7mapoverlay[data-href*='popups']",
        );
        if (!currentTarget) return;
        if (e.code === "Space") e.preventDefault();
        if (e.code === "Enter" || e.code === "Space") {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.openPopup(e, currentTarget).catch(console.error);
        }
      },
      { signal },
    );

    // Video search results — click
    document.addEventListener(
      "click",
      (e: MouseEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLElement>(
          ".js-searchresult2-video, .resource-link--video",
        );
        if (currentTarget) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.openPopup(e, currentTarget).catch(console.error);
        }
      },
      { signal },
    );

    // Video search results — keydown Enter
    document.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLElement>(
          ".js-searchresult2-video, .resource-link--video",
        );
        if (!currentTarget) return;
        if (e.code === "Enter") {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.openPopup(e, currentTarget).catch(console.error);
        }
      },
      { signal },
    );

    // Booking links
    document.addEventListener(
      "click",
      (e: MouseEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLElement>(".booking-link");
        if (currentTarget) {
          e.preventDefault();
          this.openBooking(currentTarget);
        }
      },
      { signal },
    );

    // Image resource links
    document.addEventListener(
      "click",
      (e: MouseEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLAnchorElement>(
          ".resource-link--image",
        );
        if (!currentTarget) return;
        e.preventDefault();
        const img = document.createElement("img");
        img.classList.add("lightbox__image");
        img.src = currentTarget.href;
        window.Lightbox?.openModal(img);
      },
      { signal },
    );

    // Lightbox image links (a[data-rel^='lightbox'])
    document.addEventListener(
      "click",
      (e: MouseEvent) => {
        const currentTarget = (e.target as Element).closest<HTMLAnchorElement>(
          "a[data-rel^='lightbox']",
        );
        if (!currentTarget) return;
        e.preventDefault();
        const img = document.createElement("img");
        const title = currentTarget.getAttribute("title");
        img.classList.add("lightbox__image");
        img.src = currentTarget.href;
        window.Lightbox?.openModal(img);
        if (title?.length) {
          const titleElement = document.createElement("div");
          titleElement.innerText = title;
          titleElement.classList.add("lightbox-title");
          window.Lightbox?.addContentToModal(titleElement);
        }
      },
      { signal },
    );
  }

  // ─── Hotspot link detection ───────────────────────────────────────────────────

  private isHotspotLink(element: HTMLElement): boolean {
    return (
      element.classList.contains("s7mapoverlay") &&
      !!element.dataset.href &&
      element.dataset.href.indexOf("popups") >= 0
    );
  }

  // ─── Open popup (fetch-based, replaces jQuery $.fn.load) ─────────────────────

  private async openPopup(event: Event, currentTarget: HTMLElement): Promise<void> {
    if (!this.popupContainer) return;

    const url = currentTarget.dataset.href || currentTarget.getAttribute("href") || "";
    this.movieUrl = currentTarget.dataset.resourceMovieUrl;
    this.popupContainer.innerHTML = "";
    this.popupContainer.removeAttribute("style");

    // Replaces: this.$popupContainer.load(url + " #js-popup-content", callback)
    // jQuery loads url, extracts #js-popup-content and inserts its outerHTML, then calls callback
    let statusIsError = false;
    let errorStatus = 0;
    let errorStatusText = "";

    try {
      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const fragment = doc.querySelector("#js-popup-content");
        if (fragment) {
          this.popupContainer.innerHTML = fragment.outerHTML;
        }
      } else {
        statusIsError = true;
        errorStatus = response.status;
        errorStatusText = response.statusText;
      }
    } catch (err) {
      statusIsError = true;
      errorStatusText = String(err);
    }

    // ── lightboxHandlerV2 ─────────────────────────────────────────────────────

    const ajaxPopup = this.popupContainer.querySelector<HTMLElement>("#js-popup-content");
    const images = Array.from(this.popupContainer.querySelectorAll<HTMLImageElement>("img"));
    images.forEach((img) => { img.loading = "eager"; });

    const lightboxHandlerV2 = (): void => {
      const windowMaxHeight = window.innerHeight;
      const dataMaxHeight = parseInt(ajaxPopup?.dataset.maxHeight ?? "0") || 0;
      const dataMinHeight = parseInt(ajaxPopup?.dataset.minHeight ?? "0") || 0;
      const usedMaxHeight =
        windowMaxHeight >= dataMaxHeight && dataMaxHeight !== 0
          ? dataMaxHeight
          : windowMaxHeight;
      const height = usedMaxHeight;

      if (statusIsError) {
        const message = document.createElement("span");
        const error = document.createElement("span");
        const errorContainer = document.createElement("div");
        message.innerText = "An Error occurred while loading the content: ";
        error.innerText = errorStatus + " " + errorStatusText;
        errorContainer.classList.add("error", "background-white");
        errorContainer.appendChild(message);
        errorContainer.appendChild(error);
        this.popupContainer!.appendChild(errorContainer);
      }

      window.Lightbox?.openModal(this.popupContainer!, { height: `${height}px` });
      this.removeLastMargin();
      this.popupContainer!.style.minHeight = dataMinHeight + "px";

      if (this.movieUrl !== undefined) {
        // Original: $popupContainer.find(".cq-dd-video").children(0).attr(...)
        // .children(0) is jQuery .children() (all element children — the `0` is ignored),
        // applied to every matching .cq-dd-video.
        this.popupContainer!.querySelectorAll(".cq-dd-video").forEach((video) => {
          Array.from(video.children).forEach((child) => {
            child.setAttribute("data-asset", this.movieUrl!);
            child.setAttribute("data-posterimage", this.movieUrl!);
          });
        });
      }

      window.setTimeout(() => {
        Movie.initOnElement(this.popupContainer!);
      }, 100);

      this.allowScroll();

      if (this.isHotspotLink(currentTarget)) {
        this.popupContainer!.classList.add("lightbox__hotspot");
        const title = currentTarget.getAttribute("data-title") ?? "";
        const topBar = document.createElement("div");
        const titleElement = document.createElement("div");
        const buttonElement = document.createElement("button");
        topBar.classList.add("lightbox-top-bar");
        titleElement.classList.add("lightbox-title");
        buttonElement.classList.add("lightbox-button");
        titleElement.innerText = title;
        topBar.appendChild(titleElement);
        topBar.appendChild(buttonElement);
        buttonElement.addEventListener("click", () => window.Lightbox?.closeModal());
        this.popupContainer!.querySelector(".background-white")?.prepend(topBar);
        document.getElementById("popup")?.classList.add("has-top-bar");
        if (this.detectScrollbar()) {
          document.getElementById("popup")?.classList.add("has-scrollbar");
        } else {
          document.getElementById("popup")?.classList.remove("has-scrollbar");
        }
      }
    };

    if (images.length > 0) {
      const openLightboxOnLoad = {
        opened: false,
        open: () => {
          if (!openLightboxOnLoad.opened) {
            openLightboxOnLoad.opened = true;
            lightboxHandlerV2();
          }
        },
      };
      images.forEach((img) => img.addEventListener("load", () => openLightboxOnLoad.open()));
    } else {
      lightboxHandlerV2();
    }
  }

  // ─── Open booking ─────────────────────────────────────────────────────────────

  private openBooking(currentTarget: HTMLElement): void {
    const url = currentTarget.dataset.href || currentTarget.getAttribute("href") || "";
    const iframe = document.createElement("iframe");
    iframe.id = "booking";
    iframe.src = url;
    iframe.setAttribute("data-src", url);
    window.Lightbox?.openModal(iframe);
  }

  // ─── Remove last margin ───────────────────────────────────────────────────────

  private removeLastMargin(): void {
    const viewport = document.documentElement.className;
    if (!this.popupContainer) return;

    const bgWhiteElements = Array.from(
      this.popupContainer.querySelectorAll<HTMLElement>(".background-white"),
    );
    if (bgWhiteElements.length === 0) return;

    bgWhiteElements.forEach((bgWhite) => {
      bgWhite.querySelectorAll<HTMLElement>(".parsys:not(.accordion)").forEach((element) => {
        // Add "last-section" to the last eligible direct .section child
        const sectionChildren = Array.from(
          element.querySelectorAll<HTMLElement>(
            ":scope > .section:not(.new):not(.parsys .columnControl .section)",
          ),
        );
        sectionChildren[sectionChildren.length - 1]?.classList.add("last-section");

        // Query AFTER the add above — matches jQuery's sequential DOM read in the original
        const lastColumnControls = Array.from(
          document.querySelectorAll<HTMLElement>(".columnControl.last-section"),
        );

        if (viewport.indexOf("small") >= 0 || viewport.indexOf("medium") >= 0) {
          // Original: $lastColumnControl.find("div[class^=col-]").last() → a single
          // element (the last col- across ALL matched columnControls), not per-control.
          const allColDivs = lastColumnControls.flatMap((columnControl) =>
            Array.from(
              columnControl.querySelectorAll<HTMLElement>("div[class^=col-]"),
            ),
          );
          allColDivs[allColDivs.length - 1]?.classList.add("last-section");
        } else {
          lastColumnControls.forEach((columnControl) => {
            columnControl
              .querySelectorAll<HTMLElement>("div[class^=col-]")
              .forEach((div) => div.classList.add("last-section"));
          });
        }

        // Original: $lastColumnControl.find("div[class^=col-].last-section").each(...)
        // For each such col- div, mark the LAST .section (across all its parsys) as last.
        const lastSectionColDivs = lastColumnControls.flatMap((columnControl) =>
          Array.from(
            columnControl.querySelectorAll<HTMLElement>(
              "div[class^=col-].last-section",
            ),
          ),
        );
        lastSectionColDivs.forEach((colDiv) => {
          const parsysSections = Array.from(
            colDiv.querySelectorAll<HTMLElement>(".parsys:not(.accordion)"),
          ).flatMap((parsysEl) =>
            Array.from(
              parsysEl.querySelectorAll<HTMLElement>(":scope > .section:not(.new)"),
            ),
          );
          parsysSections[parsysSections.length - 1]?.classList.add("last-section");
        });
      });
    });
  }

  // ─── Allow scroll ─────────────────────────────────────────────────────────────

  private allowScroll(): void {
    if (!this.popupContainer?.querySelector(".rich-text")) return;

    const container = document.getElementById("popup")?.parentElement;
    if (!container) return;

    // jQuery .height() returns the content-box height; getComputedStyle().height
    // is always the used content height in px (offsetHeight would wrongly add padding/border).
    const lcHeight = parseFloat(getComputedStyle(container).height) || 0;

    // Original applies to ALL .background-white elements, not just the first.
    this.popupContainer
      .querySelectorAll<HTMLElement>(".background-white")
      .forEach((bgWhite) => {
        bgWhite.style.overflow = "auto";
        bgWhite.style.overflowX = "hidden";
        bgWhite.style.maxHeight = lcHeight + 20 + "px";
      });
    // fix for content clashing with padding and showing scrollbar when not needed
    container.style.height = lcHeight + 20 + "px";
    // fix for opacity problems with overflow enabled
    container.style.opacity = "1";
  }

  // ─── Detect scrollbar ─────────────────────────────────────────────────────────

  private detectScrollbar(): boolean {
    const bgWhite = this.popupContainer?.querySelector<HTMLElement>(".background-white");
    const popup = document.getElementById("popup");
    if (!bgWhite || !popup) return false;
    return bgWhite.scrollHeight > popup.scrollHeight;
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="lightbox-controller"]')
      .forEach((el) => {
        new LightboxController(el).init();
      });
  }
}
